/**
 * Windows sign-in certificates — queries + mutation runners.
 *
 * Tenant-scoped. Feature code targets the canonical platform path shape and the api-client rewrites
 * it to /api/tenant/v1/... on a tenant host, which is where the backend mounts this surface.
 *
 * THREE PERMISSIONS, NOT TWO, WHICH IS A BACKEND FACT RATHER THAN A PREFERENCE. mountLogonCertAdmin
 * mounts three groups with RequirePermission: the reads behind auth_methods.read (5 min), the
 * revocation write behind auth_methods.manage (10 min), and the WHOLE CEREMONY SURFACE behind
 * logon_authority.manage (10 min) — a separate permission because creating an authority that can mint
 * a logon certificate for any principal in a forest is not the same grant as revoking one
 * certificate.
 *
 * SO WHICH CALLS GO THROUGH withStepUp FOLLOWS THE MOUNT, NOT AN INTUITION ABOUT WHETHER A CALL
 * "changes anything". Every call in the ceremony group can raise step_up_required, preview included,
 * so every one of them is wrapped. The GET reads are not, because withStepUp on a query would let the
 * app-wide focus refetch prompt for a passkey while the operator was reading the page — that hazard
 * is specific to refetches nobody asked for, and it does not apply to a POST an operator triggered by
 * pressing a button.
 *
 * NEITHER READ POLLS. Readiness changes when a directory sync runs or a ceremony happens, and
 * revocations change when a human acts; both are minutes-to-days facts, and a timer on either would
 * refetch hundreds of times to observe nothing. The app-wide focus refetch is the right freshness
 * here — an operator returning to the tab gets current numbers.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import { toBase64 } from "./lib/pem";
import {
  ceremonyCreatedSchema,
  ceremonyPreviewSchema,
  certificateProfileSchema,
  importPreviewSchema,
  importedAuthoritySchema,
  pendingCSRListSchema,
  pendingCSRSchema,
  promoteResultSchema,
  readinessSchema,
  setCertificateProfileResultSchema,
  trustPacketSchema,
  revocationsResponseSchema,
  type CeremonyCreated,
  type CeremonyPreview,
  type CertificateProfile,
  type ImportPreview,
  type ImportedAuthority,
  type PendingCSR,
  type PromoteResult,
  type Readiness,
  type TrustPacket,
  type Revocation,
  type RevokeInput,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/logon-certificates`;
}

// ── Reads ─────────────────────────────────────────────────────────────────

export async function fetchReadiness(tenantID: string): Promise<Readiness> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/readiness`);
  return readinessSchema.parse(raw);
}

export async function fetchRevocations(tenantID: string): Promise<Revocation[]> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/revocations?limit=100`);
  return revocationsResponseSchema.parse(raw).revocations;
}

/**
 * The trust-distribution packet for ONE forest.
 *
 * Keyed by forest so two forests' packets never share a cache entry — the mistake this whole area guards
 * against is publishing one forest's authority into another's NTAuth, and a page that could render a stale
 * neighbour's certificate would be a way to do exactly that.
 *
 * CACHED NORMALLY, AND THAT IS DELIBERATE. The "secrets are never written to any cache" rule governs
 * one-time reveals — signing secrets, API keys, recovery codes. A CA certificate is public and immutable
 * once minted, so treating it as a secret would add a pointless refetch to a page an operator reads slowly.
 */
export async function fetchTrustPacket(tenantID: string, forestDNS: string): Promise<TrustPacket> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/forests/${encodeURIComponent(forestDNS)}/trust`,
  );
  return trustPacketSchema.parse(raw);
}

export function useTrustPacket(tenantID: string, forestDNS: string) {
  return useQuery({
    queryKey: queryKeys.logonCertificates.trust(tenantID, forestDNS),
    queryFn: () => fetchTrustPacket(tenantID, forestDNS),
    enabled: forestDNS.length > 0,
  });
}

export function useReadiness(tenantID: string) {
  return useQuery({
    queryKey: queryKeys.logonCertificates.readiness(tenantID),
    queryFn: () => fetchReadiness(tenantID),
  });
}

export function useRevocations(tenantID: string) {
  return useQuery({
    queryKey: queryKeys.logonCertificates.revocations(tenantID),
    queryFn: () => fetchRevocations(tenantID),
  });
}

/**
 * The certificate profile version: which one is selected, which are selectable on this deployment,
 * what the offline window resolves to, and what is still outstanding under each version.
 *
 * NO POLLING, for the same reason as the other two reads, and one more: the drain it reports moves on
 * a scale of hours to days, and a timer would refetch hundreds of times to watch a countdown tick.
 * The focus refetch is the right freshness — an operator coming back to check whether a rollback has
 * finished draining gets a current answer when they look.
 */
export async function fetchCertificateProfile(tenantID: string): Promise<CertificateProfile> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/profile`);
  return certificateProfileSchema.parse(raw);
}

export function useCertificateProfile(tenantID: string) {
  return useQuery({
    queryKey: queryKeys.logonCertificates.profile(tenantID),
    queryFn: () => fetchCertificateProfile(tenantID),
  });
}

// ── Write ─────────────────────────────────────────────────────────────────

/**
 * Revoke one certificate.
 *
 * INVALIDATES BOTH READS, not just the list. A revocation cannot change readiness today, but the two
 * are the same operator's mental model of "is this working", and a page where half the numbers moved
 * is a page nobody trusts.
 *
 * The 404 and 409 the server returns are meaningful and are surfaced by the caller rather than
 * flattened into "something went wrong": an unknown serial almost always means a typo, and an
 * already-revoked one means the operator's intent is already satisfied.
 */
export function useRevokeCertificate(tenantID: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RevokeInput) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/revocations`, {
          method: "POST",
          // NOT JSON.stringify: apiFetch serializes `body` itself, so a pre-stringified object sends the
          // JSON STRING as the request body and the server rejects it on decode — a 400 on every
          // attempt, invisible to typecheck, lint and a network-free test suite.
          body: {
            serial: input.serial,
            reason: input.reason,
            ...(input.note ? { note: input.note } : {}),
          },
        }),
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.logonCertificates.revocations(tenantID),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.logonCertificates.readiness(tenantID),
        }),
      ]);
    },
  });
}

// ── The mint ceremony ─────────────────────────────────────────────────────

/**
 * Preview: run every gate, generate nothing.
 *
 * A MUTATION RATHER THAN A QUERY, despite reading. It is a POST because the forest is input rather
 * than a resource identifier, it must never be retried automatically or served from cache, and it is
 * a deliberate act — an operator asks for it once per attempt. A useQuery here would re-run it on
 * focus and refetch a census the operator is in the middle of reading.
 *
 * THROUGH withStepUp, because the SERVER GATES THIS CALL. The whole ceremony group — preview
 * included — sits behind RequirePermission(logon_authority.manage) with a 10-minute freshness window
 * (mountLogonCertAdmin in cmd/pureidd/routes.go), so an operator whose last assertion is older than
 * that gets step_up_required on preview itself.
 *
 * This was NOT wrapped, on the reasoning that "preview changes nothing, and a step-up prompt in the
 * middle of reading a census is the same mistake as prompting during a background refetch". The
 * conclusion did not follow from the premise, and the cost was paid by the operator: the wizard
 * reported "Could not check this forest. Try again." for a session that simply needed a fresh
 * assertion — advice that cannot work, for a cause it does not name.
 *
 * The refetch analogy is what misled it. withStepUp is REACTIVE: it prompts only when the response
 * is StepUpRequiredError, retries once, and bubbles up rather than looping. The hazard it warns about
 * is an UNSOLICITED prompt appearing while someone reads — a background refetch. This call is the
 * opposite: the operator pressed a button and is waiting for exactly this answer, which is the moment
 * a step-up dialog belongs. "Changes nothing" governs whether the SERVER should gate a call; it says
 * nothing about whether the CLIENT should handle the gate the server already has.
 */
/**
 * Preview: ask the directory what it holds, before anything is created.
 *
 * `acknowledgeUnverifiedForest` is sent on the PREVIEW as well as the create, and that is load-bearing
 * rather than symmetry. A forest whose typo signature is unacknowledged makes every preview refuse, and
 * a create requires the census digest of a preview that SUCCEEDED — so without it the gate has no route
 * past it from this surface at all. The second preview comes back carrying the server's
 * "UNVERIFIED FOREST, ACKNOWLEDGED …" warning, which is what the operator confirms against.
 */
export async function previewAuthority(
  tenantID: string,
  forest: string,
  acknowledgeUnverifiedForest?: string,
): Promise<CeremonyPreview> {
  const raw = await withStepUp(() =>
    apiFetch<unknown>(`${base(tenantID)}/authorities/preview`, {
      method: "POST",
      body: {
        forest,
        ...(acknowledgeUnverifiedForest
          ? { acknowledge_unverified_forest: acknowledgeUnverifiedForest }
          : {}),
      },
    }),
  );
  return ceremonyPreviewSchema.parse(raw);
}

export function usePreviewAuthority(tenantID: string) {
  return useMutation({
    mutationFn: (input: { forest: string; acknowledgeUnverifiedForest?: string }) =>
      previewAuthority(tenantID, input.forest, input.acknowledgeUnverifiedForest),
  });
}

/**
 * Create: mint the hierarchy.
 *
 * THROUGH withStepUp, because this is the only call on this page that creates an authority — and
 * `censusDigest` is required, so a create cannot be issued without a preview having been fetched.
 *
 * The resolved value carries the handover packet and is deliberately NOT written to any cache: it
 * belongs to one ceremony, and a cached copy would outlive the moment it describes. What IS
 * invalidated is readiness, because a new forest card must appear.
 */
export function useCreateAuthority(tenantID: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      forest: string;
      censusDigest: string;
      acknowledgeNestedForest?: string;
      acknowledgeUnverifiedForest?: string;
    }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/authorities`, {
          method: "POST",
          body: {
            forest: input.forest,
            census_digest: input.censusDigest,
            ...(input.acknowledgeNestedForest
              ? { acknowledge_nested_forest: input.acknowledgeNestedForest }
              : {}),
            // Sent on the create as well, because the mint re-runs every gate: the preview's
            // acknowledgement does not carry across the two requests.
            ...(input.acknowledgeUnverifiedForest
              ? { acknowledge_unverified_forest: input.acknowledgeUnverifiedForest }
              : {}),
          },
        }),
      ).then((raw) => ceremonyCreatedSchema.parse(raw) satisfies CeremonyCreated),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.logonCertificates.readiness(tenantID),
      });
    },
  });
}

// ── The import ceremony ───────────────────────────────────────────────────

/**
 * Preview an imported certificate: run every forest gate AND every certificate check, hold nothing.
 *
 * A MUTATION RATHER THAN A QUERY, for exactly the reasons usePreviewAuthority is one — read its
 * comment. It is a POST because the certificate is input rather than a resource identifier, it must
 * never be retried automatically or served from cache, and the operator asks for it once per attempt.
 *
 * THROUGH withStepUp, because the whole ceremony group sits behind logon_authority.manage with a
 * ten-minute freshness window, this endpoint included.
 *
 * THE WHOLE .pfx TRAVELS, AND THAT IS A NARROWING OF B8 THE SERVER DOCUMENTS. The split's property was
 * that no key left the operator's machine until every public-material refusal had been delivered; what
 * it cost was three inputs for the one file a PKI handover actually contains. The key now goes to the
 * operator's own backend, over this same stepped-up session, and is parsed, verified and zeroed without
 * being stored — so a refusal still costs nothing but the round trip. What is GAINED is that the
 * key↔certificate check answers here rather than reporting `pending`: a container whose two halves
 * disagree is caught on the screen where a different file can still be chosen.
 *
 * READ ONCE, INSIDE THIS FUNCTION, exactly as the import reads it — see useImportAuthority for why
 * that placement is the control and what each alternative would cost.
 */
export async function previewImport(
  tenantID: string,
  input: {
    forest: string;
    /** The .pfx as the wizard holds it. Read here, at the moment the body is built, and nowhere else. */
    file: File;
    password: string;
    acknowledgeUnverifiedForest?: string;
    /**
     * This is a REPLACEMENT for the forest's active issuing CA (B11), not its first authority.
     *
     * SENT AS STATED INTENT rather than inferred by the server from the forest already being served,
     * because it inverts the idempotence gate — the refusal that catches a mistyped forest on a second
     * onboarding. The wizard only sets it when the operator arrived through "Replace certificate".
     */
    replace?: boolean;
  },
): Promise<ImportPreview> {
  // READ ONCE, HERE, AND HELD ONLY AS THIS LOCAL. Outside the withStepUp task rather than inside it so
  // a step-up retry re-sends the same bytes instead of re-reading a File whose handle may by then be
  // stale — the same placement the import uses, for the same reason.
  const pkcs12Base64 = toBase64(await input.file.arrayBuffer());
  const raw = await withStepUp(() =>
    apiFetch<unknown>(`${base(tenantID)}/authorities/import/preview`, {
      method: "POST",
      body: {
        forest: input.forest,
        pkcs12_base64: pkcs12Base64,
        password: input.password,
        ...(input.replace ? { replace: true } : {}),
        // Sent on the preview as well as the import, and load-bearing rather than symmetry: an
        // unacknowledged typo signature makes every preview refuse, and the import requires the census
        // digest of a preview that SUCCEEDED. See previewAuthority.
        ...(input.acknowledgeUnverifiedForest
          ? { acknowledge_unverified_forest: input.acknowledgeUnverifiedForest }
          : {}),
      },
    }),
  );
  return importPreviewSchema.parse(raw);
}

export function usePreviewImport(tenantID: string) {
  return useMutation({
    mutationFn: (input: {
      forest: string;
      file: File;
      password: string;
      acknowledgeUnverifiedForest?: string;
      replace?: boolean;
    }) => previewImport(tenantID, input),
  });
}

// ── CSR-out: PureID generates the key ────────────────

/**
 * Ask PureID to generate the key and hand back a signing request.
 *
 * A MUTATION AND NOT A QUERY, emphatically: it generates and stores a CA private key. Running on focus
 * refetch, or being retried automatically, would leave a trail of sealed keys — each one superseding
 * the last and invalidating whatever the customer's PKI team was already holding.
 *
 * THROUGH withStepUp, because it sits in the ceremony group behind logon_authority.manage. Generating
 * the authority's key is the same decision as bringing one.
 *
 * INVALIDATES THE PENDING LIST, which is the surface an operator returns to weeks later — and after a
 * supersede the old entry must be gone from it, or the list would offer a request whose key no longer
 * exists.
 */
export function useRequestCSR(tenantID: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { forest: string; replace?: boolean; acknowledgeUnverifiedForest?: string }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/authorities/csr`, {
          method: "POST",
          body: {
            forest: input.forest,
            ...(input.replace ? { replace: true } : {}),
            ...(input.acknowledgeUnverifiedForest
              ? { acknowledge_unverified_forest: input.acknowledgeUnverifiedForest }
              : {}),
          },
        }),
      ).then((raw) => pendingCSRSchema.parse(raw)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.logonCertificates.pendingCSRs(tenantID),
      });
    },
  });
}

/**
 * The outstanding signing requests.
 *
 * A QUERY, unlike its sibling above, because this one only reads — and it is the read that makes the
 * ceremony's second half possible at all: it is how an operator coming back with a signed certificate
 * finds the request id and re-fetches the CSR they lost.
 *
 * NOT POLLED. A request changes when a human generates or redeems one, and its 30-day clock does not
 * need watching by a timer. The app-wide focus refetch is the right freshness.
 *
 * CACHED NORMALLY AND THAT IS SAFE: a signing request is public material. The "secrets never enter a
 * cache" rule governs one-time reveals, and the private half this request belongs to is sealed on the
 * backend and never travels.
 */
export async function fetchPendingCSRs(tenantID: string): Promise<PendingCSR[]> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/authorities/csr`);
  return pendingCSRListSchema.parse(raw).pending;
}

export function usePendingCSRs(tenantID: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.logonCertificates.pendingCSRs(tenantID),
    queryFn: () => fetchPendingCSRs(tenantID),
    enabled,
  });
}

export interface ImportAuthorityInput {
  forest: string;
  /** From the preview. The server recomputes it; a directory that moved forces a fresh preview. */
  censusDigest: string;
  /**
   * From the preview. The server checks it against the certificate INSIDE the file sent below, so a
   * different .pfx chosen after the review is refused rather than silently imported.
   */
  certificateDigest: string;
  /** The same file and password the preview was run against. No certificate or chain travels beside it. */
  file: File;
  password: string;
  acknowledgeSelfSignedRoot?: string;
  acknowledgeNestedForest?: string;
  acknowledgeUnverifiedForest?: string;
  importReference?: string;
  /** See previewImport: sent again here because the import re-runs every gate, intent included. */
  replace?: boolean;
}

/**
 * Import: the customer's certificate and its private key become this forest's issuing authority.
 *
 * ── WHY THE SECRET IS READ HERE AND NOWHERE ELSE ────────────────────────────
 *
 * The `File` and the password are held in the wizard's component state and are read INSIDE this
 * function, at the moment the request body is built. That placement is the whole control, and it is
 * worth stating what each alternative would cost:
 *
 *   * Reading the file when it is chosen would put the key's BYTES in component state for as long as
 *     the operator spends on the remaining steps — minutes, on a screen they may walk away from.
 *   * Reading it in a `useQuery` — or writing the result anywhere TanStack Query can see it — would put
 *     a CA private key in a cache that outlives the ceremony, survives navigation, and is
 *     garbage-collected on a timer rather than on purpose. The "secrets never enter a cache" rule
 *     exists for one-time reveals; a key that can authenticate any principal in a Windows forest,
 *     Domain Admins included, is the strongest case of it in this product.
 *   * Passing it through a store or a router search param would put it somewhere durable. It is neither
 *     in the URL nor in any store: it is an argument to this function and a local inside it.
 *
 * So the material exists as plaintext in this process between `arrayBuffer()` and the `fetch` that
 * sends it, and nowhere else. The RESOLVED VALUE carries no key material either — it is the trust
 * packet, which is public — and is deliberately not cached: it belongs to one ceremony. What IS
 * invalidated is readiness, because a new forest card must appear.
 *
 * A STEP-UP MID-UPLOAD RE-RUNS THIS FUNCTION with the same in-memory `File`, never a stored copy —
 * withStepUp retries the task, and the task is what reads the bytes.
 */
export function useImportAuthority(tenantID: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ImportAuthorityInput): Promise<ImportedAuthority> => {
      // READ HERE, ONCE, AT THE LAST POSSIBLE MOMENT. Binary, so it travels base64 — never re-encoded
      // as text, which is a way to corrupt key material that no test on this side would catch.
      const key = {
        pkcs12_base64: toBase64(await input.file.arrayBuffer()),
        password: input.password,
      };

      const raw = await withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/authorities/import`, {
          method: "POST",
          // NOT JSON.stringify: apiFetch serializes `body` itself.
          body: {
            forest: input.forest,
            census_digest: input.censusDigest,
            certificate_digest: input.certificateDigest,
            // NO certificate_pem AND NO chain_pem: both are inside the container, and the server takes
            // them from there. Sending a second copy would create a pair that could disagree.
            key,
            ...(input.acknowledgeSelfSignedRoot
              ? { acknowledge_self_signed_root: input.acknowledgeSelfSignedRoot }
              : {}),
            // The import re-runs every forest gate, so the acknowledgements the preview accepted must
            // be sent again — they do not carry across the two requests.
            ...(input.acknowledgeNestedForest
              ? { acknowledge_nested_forest: input.acknowledgeNestedForest }
              : {}),
            ...(input.acknowledgeUnverifiedForest
              ? { acknowledge_unverified_forest: input.acknowledgeUnverifiedForest }
              : {}),
            ...(input.importReference ? { import_reference: input.importReference } : {}),
            ...(input.replace ? { replace: true } : {}),
          },
        }),
      );
      return importedAuthoritySchema.parse(raw);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.logonCertificates.readiness(tenantID),
      });
    },
  });
}

/**
 * Choose the certificate profile version.
 *
 * THROUGH withStepUp, because the route is mounted behind auth_methods.manage with a ten-minute
 * freshness window — the same gate revocation sits behind, and for the same reason: this changes what
 * this organisation's certificates mean.
 *
 * The 400 and 409 the server returns are meaningful and are surfaced by the caller rather than
 * flattened. A 400 means the deployed build does not implement the version, so the fix is a
 * deployment; a 409 means it does but this deployment's revocation list cannot cover it, and the fix
 * is a different configuration change. Collapsing them sends an operator to the wrong person.
 */
export function useSetCertificateProfile(tenantID: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (version: number) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/profile`, {
          method: "PUT",
          body: { version },
        }),
      ).then((raw) => setCertificateProfileResultSchema.parse(raw)),
    onSuccess: async () => {
      // THE DRAIN CHANGED THE MOMENT THE CEILING DID — not the certificates already issued, but which
      // version the console must now describe as the one being left behind. Refetching is what keeps
      // the warning naming the right deadline.
      await queryClient.invalidateQueries({
        queryKey: queryKeys.logonCertificates.profile(tenantID),
      });
    },
  });
}

// ── Governing an authority that already exists ────────────────────────────
//
// ALL FIVE THROUGH withStepUp AND ALL FIVE INVALIDATE READINESS. Each changes who can sign in to
// Windows, which is the same magnitude of decision whichever direction it moves — and each changes what
// a forest card should say, so a card left showing the old state after a successful action is a card
// nobody trusts.
//
// The forest is a PATH segment (its DNS name) rather than a body field, because these act on a resource
// that already exists and the server resolves it through this tenant's own rows — so a forest this
// tenant does not serve is a 404 rather than a lookup that reaches somebody else's hierarchy.

function forestBase(tenantID: string, forestDNS: string): string {
  return `${base(tenantID)}/forests/${encodeURIComponent(forestDNS)}`;
}

// TResult defaults to unknown, which is what four of these want: their responses are confirmations the
// caller does not read. Promote is the exception — its body carries the coverage count and the note the
// operator has to see — so the parameter exists rather than every caller re-parsing an `unknown`.
function useForestAction<TInput, TResult = unknown>(
  tenantID: string,
  run: (input: TInput) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => withStepUp(() => run(input)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.logonCertificates.readiness(tenantID),
      });
    },
  });
}

/** Record a domain as covered. No certificate changes and no NTAuth work — see the server's comment. */
export function useAddDomain(tenantID: string) {
  return useForestAction<{ forestDNS: string; domain: string }>(tenantID, (input) =>
    apiFetch<unknown>(`${forestBase(tenantID, input.forestDNS)}/domains`, {
      method: "POST",
      body: { domain: input.domain },
    }),
  );
}

/** Withdraw coverage. Quiet and immediate: accounts in that domain are refused at the next attempt. */
export function useRemoveDomain(tenantID: string) {
  return useForestAction<{ forestDNS: string; domain: string }>(tenantID, (input) =>
    apiFetch<unknown>(`${forestBase(tenantID, input.forestDNS)}/domains`, {
      method: "DELETE",
      body: { domain: input.domain },
    }),
  );
}

/** Tier 0: stop issuing for one forest. A reason is required and recorded. */
export function useSuspendForest(tenantID: string) {
  return useForestAction<{ forestDNS: string; reason: string }>(tenantID, (input) =>
    apiFetch<unknown>(`${forestBase(tenantID, input.forestDNS)}/suspend`, {
      method: "POST",
      body: { reason: input.reason },
    }),
  );
}

/** Undo a suspension. No reason required, matching the server and the CLI. */
export function useResumeForest(tenantID: string) {
  return useForestAction<{ forestDNS: string }>(tenantID, (input) =>
    apiFetch<unknown>(`${forestBase(tenantID, input.forestDNS)}/resume`, {
      method: "POST",
      body: {},
    }),
  );
}

/** Terminal. Releases coverage, and cannot be undone. */
export function useRetireForest(tenantID: string) {
  return useForestAction<{ forestDNS: string; reason: string }>(tenantID, (input) =>
    apiFetch<unknown>(`${forestBase(tenantID, input.forestDNS)}/retire`, {
      method: "POST",
      body: { reason: input.reason },
    }),
  );
}

/**
 * Record that this forest's authority reached NTAuth.
 *
 * THE ACTOR IS NOT SENT. The server takes it from the session, because the whole value of this row is
 * "who said this happened" and a client-supplied name would make the one field that matters the one
 * field the caller controls. Only their change reference travels.
 */
export function useRecordNTAuth(tenantID: string) {
  return useForestAction<{ forestDNS: string; generation?: number; reference?: string }>(
    tenantID,
    (input) =>
      apiFetch<unknown>(`${forestBase(tenantID, input.forestDNS)}/ntauth`, {
        method: "POST",
        body: {
          reference: input.reference ?? "",
          // THE GENERATION IS SENT DURING A ROLLOVER AND OMITTED OTHERWISE. Zero means "the active one"
          // to the server, which is the only answer outside a rollover — but during one the generation
          // whose publication matters is the one that is NOT active yet, because that publication is
          // what promotion is gated on (B11).
          ...(input.generation ? { generation: input.generation } : {}),
        },
      }),
  );
}

/**
 * Hand a forest over to the generation waiting in `verifying` — the second half of make-before-break
 *.
 *
 * THE GENERATION IS ALWAYS SENT AND NEVER DEFAULTED, mirroring the server, which refuses without it.
 * The other forest actions address "this forest's authority" and have one answer; this one addresses
 * the certificate that is about to start signing for every account in the forest, and a number the
 * client chose is a number nobody checked.
 *
 * `acknowledgeUnpublished` is sent only when the operator answered the NTAuth refusal. Sending it
 * unconditionally is refused by the server, which is the point: an override accepted whether or not it
 * applies stops being an override.
 *
 * INVALIDATES READINESS like its siblings — both generations' rows change status, and a card still
 * showing the old signer after a successful rollover is a card nobody trusts.
 */
export function usePromoteGeneration(tenantID: string) {
  return useForestAction<
    { forestDNS: string; generation: number; acknowledgeUnpublished?: string },
    PromoteResult
  >(
    tenantID,
    (input) =>
      apiFetch<unknown>(`${forestBase(tenantID, input.forestDNS)}/promote`, {
        method: "POST",
        body: {
          generation: input.generation,
          ...(input.acknowledgeUnpublished
            ? { acknowledge_unpublished: input.acknowledgeUnpublished }
            : {}),
        },
      }).then((raw) => promoteResultSchema.parse(raw)),
  );
}

/** Withdraw the record, because people record the wrong forest or record it before the window closes. */
export function useClearNTAuth(tenantID: string) {
  return useForestAction<{ forestDNS: string; generation?: number }>(tenantID, (input) =>
    apiFetch<unknown>(`${forestBase(tenantID, input.forestDNS)}/ntauth`, {
      method: "DELETE",
      // Same rule as recording it: named during a rollover, defaulted outside one.
      body: input.generation ? { generation: input.generation } : {},
    }),
  );
}
